import { FormRenderProps, FormHandle } from '@progress/kendo-react-form'
import { DEFAULT_PERMISSION_MESSAGE, GENERIC_UNKNOWN_ERROR_MESSAGE } from './consts'
import Cookies from 'universal-cookie'
import { HTTP } from './http-common'
import { hasPermission, hasAnyPermission } from 'state/reduxHelpers'
import { DcsPermissions, GridItemDeleteEnum, TypeEnum } from 'types'
import { toast } from 'core/toast'
import moment from 'moment'
import { typeData } from './TypeData'
import { useEffect, useRef, useState } from 'react'
import { AttachmentTypeEnum } from 'generated/v1/v1SharedApi'
const cookies = new Cookies()

export const setToken = (token: string) => {
    cookies.set(process.env.REACT_APP_COOKIE_NAME as string, token, {
        path: '/',
        domain: process.env.NODE_ENV === 'production' ? process.env.REACT_APP_COOKIE_DOMAIN : undefined,
        sameSite: 'lax'
    })
    HTTP.defaults.headers.common.Authorization = `Bearer ${token}`
}

/**
 * Clear the token from the cookie and from the authorization header
 */
export const clearToken = () => {
    cookies.remove(process.env.REACT_APP_COOKIE_NAME as string, {
        path: '/',
        domain: process.env.NODE_ENV === 'production' ? process.env.REACT_APP_COOKIE_DOMAIN : undefined
    })
    delete HTTP.defaults.headers.common.Authorization
}

/**
 * @returns the token retrived from the cookie and set to the authorization header
 */
export const getToken = () => {
    const token = cookies.get(process.env.REACT_APP_COOKIE_NAME as string)
    HTTP.defaults.headers.common.Authorization = `Bearer ${token}`
    return token
}

/**
 * Sets focus to first field with a validation error
 * Requires that any field with validation contain an id attribute
 * @param formProps properties of the form
 */
export const focusFirstError = (formProps: FormRenderProps) => {
    if (!formProps.valid) {
        let errorId: string = ''
        for (const prop in formProps.errors) {
            if (formProps.errors[prop]) {
                errorId = prop
                break
            }
        }
        if (errorId != '') {
            const element = document.getElementById(errorId) || document.getElementsByName(errorId)[0]
            element?.focus()
            if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
                element.select()
            }
        }
    }
}


/**
* Sets focus to first field with a validation error
* Requires that any field with validation contain an id attribute
* @param form properties of the form
*/
export const focusFirstErrorByForm = async (form: FormHandle) => {
    if (!form.isValid()) {
        let errorId: string = ''
        for (const prop in form.errors) {
            if (form.errors[prop]) {
                errorId = prop
                break
            }
        }
        if (errorId != '') {
            const element = document.getElementById(errorId) || document.getElementsByName(errorId)[0]
            element?.focus()
            if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
                element.select()
            }
        }
    }
}

/** 
* - getErrors - try to get api response errors, fallback axios error, or JS exception
*/
export const getErrors = (e: unknown): string[] => {
    if ((e as Record<string, unknown>).hasOwnProperty('errors')) {
        return Array.from((e as Record<string, unknown>).errors as Iterable<string>)
    }
    if (e instanceof Error) {
        return [e.message]
    }
    return [GENERIC_UNKNOWN_ERROR_MESSAGE]
}

/**
 * sleep - a promise that waits for a period of time
 * @param {number} ms - The amount of time to wait in milliseconds
 */
export const sleep = (ms: number) => new Promise((resolve) => {
    setTimeout(resolve, ms)
})

export const hasStatusCode = (e: unknown, statusCode: number): boolean => {
    if (!e)
        return false

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof (e as any) === 'number') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (e as number) === statusCode
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof e === 'object' && 'statusCode' in e && typeof (e as any).statusCode === 'number') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (e as any).statusCode === statusCode
    }
    return false
}

/**
 * Display a number as currency
 *  
 *   *  * @param param: number to convert to currency
 *   *  * @param maximumFractionDigits: maximum amount of fraction digits returned, defaulted to zero
 */

export function numberToCurrency(param: number | undefined, maximumFractionDigits = 0) {
    if (param === undefined)
        param = 0
    var formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: maximumFractionDigits
    })
    return formatter.format(param)
}

/**
 * Redirects user if the they any of these permissions
 *  * @param permissions: an Array of DcsPermissions to check
 *  * @param redirectUrl: url to redirect to if the user has permission
 *  * @param errorMessage: optional error message to display via toast, else a default will be shown
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const anyPermissionsRedirect = (permissions: Array<DcsPermissions>, redirectUrl: string, navigate: any, errorMessage?: string, params?: unknown) => {
    if (!hasAnyPermission(permissions)) {
        toast.error(errorMessage ? errorMessage : DEFAULT_PERMISSION_MESSAGE)
        return
    }
    if (redirectUrl.includes('.aspx'))
        location.href = redirectUrl
    else
        navigate(redirectUrl, params)
}

/**
 * Determine delete type based on data from a grid item.
 *
 * @param hasPermission Does the user have permission to delete.
 * @param dealerId Dealer Id assoicated with the item.
 * @param inUse Is the item in use.
 */
export const getDeleteType = (hasPermission: boolean, dealerId: number, inUse: boolean) => {
    let itemDeleteType: GridItemDeleteEnum = GridItemDeleteEnum.CanBeDeleted
    if (!hasPermission)
        return GridItemDeleteEnum.CantBeDeletedPermission
    else if (inUse)
        return GridItemDeleteEnum.CantBeDeletedInUse
    else if (dealerId === 0)
        return GridItemDeleteEnum.CantBeDeletedDefaultRecord
    return itemDeleteType
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const permissionRedirect = (permission: DcsPermissions, redirectUrl: string, navigate: any, errorMessage?: string, params?: unknown) => {
    if (!hasPermission(permission)) {
        toast.error(errorMessage ? errorMessage : DEFAULT_PERMISSION_MESSAGE)
        return
    }
    if (redirectUrl.includes('.aspx'))
        location.href = redirectUrl
    else
        navigate(redirectUrl, params)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchTypeData = async (type: TypeEnum, id?: number, otherTypeParams?: any) => {
    switch (type) {
        case TypeEnum.caseStatus:
            return await typeData.caseStatuses()
        case TypeEnum.caseSubStatus:
            return await typeData.caseSubStatuses(id)
        case TypeEnum.caseType:
            return await typeData.caseTypes()
        case TypeEnum.caseReason:
            return await typeData.caseReason()
        case TypeEnum.caseSubReason:
            return await typeData.caseSubReasons(id)
        case TypeEnum.casePriority:
            return await typeData.casePriorities()
        case TypeEnum.caseOrigin:
            return await typeData.caseOrigins()
        case TypeEnum.activeUser:
            return await typeData.activeUsers()
        case TypeEnum.allUsers:
            return await typeData.allUsers()
        case TypeEnum.dealerContact:
            return await typeData.dealerContacts(id)
        case TypeEnum.competitor:
            return await typeData.competitors()
        case TypeEnum.cancellationReason:
            return await typeData.cancellationReasons()
        case TypeEnum.approveCaseUser:
            return await typeData.approveCaseUsers()
        default:
            return []
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useStateRef(initialValue: any) {
    const [value, setValue] = useState(initialValue)

    const ref = useRef(value)

    useEffect(() => {
        ref.current = value
    }, [value])

    return [value, setValue, ref]
}


export function isValidYear(date: Date) {
    return date.getFullYear() > 1900
}

export const permissionMap = {
    [AttachmentTypeEnum.Dealer]: {
        add: DcsPermissions.AddAttachmentDealer,
        edit: DcsPermissions.EditAttachmentDealer,
    },
    [AttachmentTypeEnum.Case]: {
        add: DcsPermissions.AddAttachmentCase,
        edit: DcsPermissions.EditAttachmentCase,
    },
    [AttachmentTypeEnum.Task]: {
        add: DcsPermissions.AddAttachmentTask,
        edit: DcsPermissions.EditAttachmentTask,
    },
    [AttachmentTypeEnum.Lead]: {
        add: DcsPermissions.AddAttachmentLead,
        edit: DcsPermissions.EditAttachmentLead,
    },
    [AttachmentTypeEnum.Development]: {
        add: DcsPermissions.AddNoteDevelopment,
        edit: DcsPermissions.EditNoteDevelopment,
    },
    [AttachmentTypeEnum.Importer]: {
        add: DcsPermissions.AddNoteImporter,
        edit: DcsPermissions.EditNoteImporter,
    },
    [AttachmentTypeEnum.Exporter]: {
        add: DcsPermissions.AddNoteExporter,
        edit: DcsPermissions.EditNoteExporter,
    },
    [AttachmentTypeEnum.DealerContact]: {
        add: DcsPermissions.AddNoteDealerContact,
        edit: DcsPermissions.EditNoteDealerContact,
    },
}

export const toHexColor = (value: string | null | undefined): string => {
    if (!value) return '#000000'

    const rgbaMatch = value.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*[\d.]+\s*)?\)/i)
    if (rgbaMatch) {
        const [r, g, b] = rgbaMatch.slice(1, 4).map(Number)
        return `#${[r, g, b].map(c => c.toString(16).padStart(2, '0')).join('')}`
    }

    return value.startsWith('#') ? value : `#${value}`
}
